import { styled, Theme } from 'baseui'
import { FlexCenterY, FlexColumn } from '@/shared/theme/styled-components'

export const StyledContainer = styled<'div', { $mastered?: boolean }>('div', ({ $theme, $mastered }) => ({
  width: '100%',
  border: `0.1rem solid ${$mastered ? $theme.colors.warning400 : 'transparent'}`,
  borderRadius: $theme.borders.radius300,
}))

export const StyledTopContainer = styled('div', () => ({
  width: '100%',
  padding: '0 1rem 1rem 1rem',
}))

export const StyledCurrentValue = styled<'span', { $reachedMax?: boolean }>('span', ({ $theme, $reachedMax }) => ({
  color: $reachedMax ? $theme.colors.positive600 : $theme.colors.mono500,
}))

export const StyledSubtitle = styled('p', ({ $theme }) => ({
  margin: '0 0 0.75rem 0',
  fontSize: '0.85rem',
  color: $theme.colors.textSecondaryAlt,
}))

export const StyledProgressBarContainer = styled('div', () => ({
  width: '100%',
}))

export const StyledProgressTrack = styled<'div', { $mastered?: boolean }>('div', ({ $theme, $mastered }) => ({
  position: 'relative',
  width: '100%',
  height: $theme.sizing.scale400,
  borderRadius: '0.5rem',
  overflow: 'hidden',
  background: $mastered ? $theme.colors.positive500 : 'linear-gradient(90deg, #FFA051 0%, #FFD152 55%, #25A337 100%)',
}))

export const StyledProgressMask = styled<'div', { $percentDone: number }>('div', ({ $percentDone }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: `${100 - $percentDone}%`,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  transition: 'width 0.3s ease',
}))

export const StyledItemsContainer = styled('div', ({ $theme }) => ({
  borderTop: `0.1rem solid ${$theme.colors.borderSoft}`,
}))

export const StyledItemRow = styled<'div', { $done?: boolean }>('div', ({ $theme, $done }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 1rem',
  backgroundColor: $done ? $theme.colors.positive50 : 'transparent',
}))

export const StyledItemLeft = styled(FlexCenterY, {
  gap: '0.5rem',
})

export const StyledItemLabel = styled<'span', { $done?: boolean }>('span', ({ $theme, $done }) => ({
  color: $done ? $theme.colors.positive600 : $theme.colors.textHeading,
}))

export const StyledDoneIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.positive500,
}))

export const StyledPendingIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.mono500,
}))

interface PulseProp {
  $pulse?: boolean
}

const radius = (value: string) => ({
  borderTopLeftRadius: value,
  borderTopRightRadius: value,
  borderBottomLeftRadius: value,
  borderBottomRightRadius: value,
})

const border = (color: string) => ({
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderTopStyle: 'solid' as const,
  borderBottomStyle: 'solid' as const,
  borderLeftStyle: 'solid' as const,
  borderRightStyle: 'solid' as const,
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

export const StyledActions = styled(FlexColumn, {
  width: '100%',
  gap: '0.5rem',
})

export const StyledCardHeader = styled<'div', PulseProp>('div', ({ $pulse }) => ({
  display: 'flex',
  alignItems: $pulse ? 'flex-start' : 'center',
  justifyContent: 'space-between',
  gap: $pulse ? '10px' : '0.5rem',
  marginBottom: $pulse ? '12px' : '0.5rem',
}))

export const StyledCardTitle = styled<'strong', PulseProp>('strong', ({ $theme, $pulse }) =>
  $pulse
    ? { fontSize: '14px', lineHeight: '19px', fontWeight: 600, color: $theme.colors.textPulseStrong }
    : {
        fontSize: '0.95rem',
        color: $theme.colors.textHeading,
      },
)

const pulseBadgeColors = ($theme: Theme, $muted?: boolean) =>
  $muted
    ? { color: $theme.colors.textPulseMuted, backgroundColor: $theme.colors.bgPulseTrack }
    : { color: $theme.colors.primaryPulse, backgroundColor: $theme.colors.bgPulseSelected }

export const StyledBadge = styled<'span', { $muted?: boolean } & PulseProp>('span', ({ $theme, $muted, $pulse }) =>
  $pulse
    ? {
        flex: '0 0 auto',
        paddingTop: '2px',
        paddingBottom: '2px',
        paddingLeft: '9px',
        paddingRight: '9px',
        ...radius('20px'),
        fontSize: '11.5px',
        fontWeight: 600,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        ...pulseBadgeColors($theme, $muted),
      }
    : {
        flex: '0 0 auto',
        padding: '0.15rem 0.5rem',
        borderRadius: '999px',
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: $muted ? $theme.colors.textMuted : $theme.colors.white,
        backgroundColor: $muted ? $theme.colors.borderLight : $theme.colors.warning400,
      },
)

export const StyledSteps = styled<'div', PulseProp>('div', ({ $pulse }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $pulse ? '8px' : '0.35rem',
  margin: $pulse ? '0 0 12px' : '0 0 0.75rem',
}))

export const StyledStep = styled<'div', PulseProp>('div', ({ $pulse }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $pulse ? '6px' : '0.3rem',
}))

interface StepStateProps extends PulseProp {
  $done?: boolean
  $current?: boolean
}

const pulseMarkerColors = ($theme: Theme, $done?: boolean, $current?: boolean) => {
  if ($done) return { color: $theme.colors.chipOkText, backgroundColor: $theme.colors.noticeOkBg }
  if ($current) return { color: $theme.colors.white, backgroundColor: $theme.colors.primaryPulse }
  return { color: $theme.colors.textPulseMuted, backgroundColor: $theme.colors.bgPulseTrack }
}

export const StyledStepMarker = styled<'span', StepStateProps>('span', ({ $theme, $done, $current, $pulse }) =>
  $pulse
    ? {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        ...radius('9999px'),
        fontSize: '11px',
        fontWeight: 700,
        ...pulseMarkerColors($theme, $done, $current),
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1.15rem',
        height: '1.15rem',
        borderRadius: '50%',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: $done || $current ? $theme.colors.white : $theme.colors.textMuted,
        backgroundColor: $done
          ? $theme.colors.positive500
          : $current
            ? $theme.colors.primary
            : $theme.colors.borderLight,
      },
)

const pulseStepLabelColor = ($theme: Theme, $done?: boolean, $current?: boolean) => {
  if ($done) return $theme.colors.chipOkText
  if ($current) return $theme.colors.textPulseStrong
  return $theme.colors.textPulseMuted
}

export const StyledStepLabel = styled<'span', StepStateProps>('span', ({ $theme, $done, $current, $pulse }) =>
  $pulse
    ? {
        fontSize: '12.5px',
        whiteSpace: 'nowrap',
        fontWeight: $current ? 600 : 400,
        color: pulseStepLabelColor($theme, $done, $current),
      }
    : {
        fontSize: '0.75rem',
        fontWeight: $current ? 700 : 400,
        color: $done || $current ? $theme.colors.textHeading : $theme.colors.textMuted,
      },
)

export const StyledStepConnector = styled<'span', { $done?: boolean } & PulseProp>(
  'span',
  ({ $theme, $done, $pulse }) =>
    $pulse
      ? {
          flex: '1 1 auto',
          minWidth: '16px',
          height: '2px',
          ...radius('2px'),
          backgroundColor: $done ? $theme.colors.successPulse : $theme.colors.borderPulse,
        }
      : {
          flex: '1 1 auto',
          height: '1px',
          backgroundColor: $done ? $theme.colors.positive500 : $theme.colors.borderLight,
        },
)

export const StyledCodeBox = styled<'div', PulseProp>('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '12px',
        paddingRight: '12px',
        marginBottom: '12px',
        ...radius('10px'),
        backgroundColor: $theme.colors.bgPulseCanvas,
        ...border($theme.colors.borderPulse),
      }
    : {
        padding: '0.75rem',
        textAlign: 'center',
        borderRadius: '8px',
        backgroundColor: $theme.colors.bgSubtle,
        border: `1px solid ${$theme.colors.borderLight}`,
      },
)

export const StyledCode = styled<'div', PulseProp>('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        fontFamily: 'ui-monospace, Consolas, monospace',
        fontSize: '28px',
        fontWeight: 700,
        letterSpacing: '0.24em',
        color: $theme.colors.textPulseStrong,
      }
    : {
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '0.2em',
        color: $theme.colors.primary,
      },
)

export const StyledCodeExpiry = styled<'p', PulseProp>('p', ({ $theme, $pulse }) =>
  $pulse
    ? { margin: 0, fontSize: '12px', color: $theme.colors.textPulseMuted }
    : {
        margin: '0.25rem 0 0.5rem',
        fontSize: '0.8rem',
        color: $theme.colors.textMuted,
      },
)

const PILL_RADIUS = {
  borderTopLeftRadius: '999px',
  borderTopRightRadius: '999px',
  borderBottomLeftRadius: '999px',
  borderBottomRightRadius: '999px',
}

// baseui rounds the bar with the four longhand radii and slides the fill with a transform, so a
// `borderRadius` shorthand loses the merge and a transition on `width` animates nothing.
export const CodeProgressOverrides = {
  BarContainer: {
    style: () => ({ width: '100%', marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 }),
  },
  Bar: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...PILL_RADIUS,
      width: '100%',
      height: '0.3rem',
      backgroundColor: $theme.colors.borderLight,
    }),
  },
  BarProgress: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...PILL_RADIUS,
      backgroundColor: $theme.colors.primary,
      transition: 'transform 1s linear',
    }),
  },
}

export const PulseCodeProgressOverrides = {
  ...CodeProgressOverrides,
  Bar: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...radius('4px'),
      width: '100%',
      height: '4px',
      backgroundColor: $theme.colors.borderPulse,
    }),
  },
  BarProgress: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...radius('4px'),
      backgroundColor: $theme.colors.primaryPulse,
      transition: 'transform 1s linear',
    }),
  },
}

export const StyledOrDivider = styled<'div', PulseProp>('div', ({ $theme, $pulse }) => {
  const lineColor = $pulse ? $theme.colors.borderPulse : $theme.colors.borderLight

  return {
    display: 'flex',
    alignItems: 'center',
    gap: $pulse ? '10px' : '0.5rem',
    margin: $pulse ? '12px 0' : '0.75rem 0',
    fontSize: $pulse ? '12px' : '0.8rem',
    color: $pulse ? $theme.colors.textPulseFaint : $theme.colors.textMuted,
    '::before': { content: '""', flex: '1 1 auto', height: '1px', backgroundColor: lineColor },
    '::after': { content: '""', flex: '1 1 auto', height: '1px', backgroundColor: lineColor },
  }
})

export const StyledInfo = styled<'p', PulseProp>('p', ({ $theme, $pulse }) =>
  $pulse
    ? { margin: '10px 0 0', fontSize: '12px', lineHeight: '17px', color: $theme.colors.textPulseMuted }
    : {
        margin: '0.5rem 0 0',
        fontSize: '0.85rem',
        lineHeight: 1.45,
        color: $theme.colors.textSecondaryAlt,
      },
)

export const StyledCodeCard = styled<'div', { $muted?: boolean } & PulseProp>('div', ({ $theme, $muted, $pulse }) =>
  $pulse
    ? {
        width: '100%',
        boxSizing: 'border-box',
        marginBottom: '12px',
        paddingTop: '14px',
        paddingBottom: '14px',
        paddingLeft: '14px',
        paddingRight: '14px',
        ...radius('10px'),
        ...border($theme.colors.borderPulse),
        backgroundColor: $muted ? $theme.colors.bgPulseCanvas : $theme.colors.white,
        color: $theme.colors.textPulseLabel,
        fontSize: '13px',
        lineHeight: '19px',
      }
    : {
        width: '100%',
        marginBottom: '0.5rem',
        padding: '0.5rem',
        borderRadius: $theme.borders.radius200,
        border: `1px dashed ${$muted ? $theme.colors.borderLight : $theme.colors.primary400}`,
        backgroundColor: $muted ? 'transparent' : $theme.colors.bg050,
        color: $theme.colors.grey,
        ...$theme.typography.ParagraphXSmall,
      },
)

export const StyledScript = styled<'p', PulseProp>('p', ({ $theme, $pulse }) =>
  $pulse
    ? { margin: '0 0 12px', fontSize: '13px', lineHeight: '19px', color: $theme.colors.textPulseLabel }
    : {
        margin: '0 0 0.5rem',
      },
)

export const StyledSentBox = styled<'div', PulseProp>('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        margin: '0 0 12px',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '10px',
        paddingRight: '10px',
        ...radius('8px'),
        backgroundColor: $theme.colors.bgPulseCanvas,
        fontSize: '12.5px',
        lineHeight: '17px',
        color: $theme.colors.textPulseLabel,
      }
    : {
        margin: '0 0 0.5rem',
        padding: '0.5rem',
        borderRadius: $theme.borders.radius200,
        backgroundColor: $theme.colors.successBg,
      },
)

export const StyledSentTitle = styled<'div', PulseProp>('div', ({ $theme, $pulse }) =>
  $pulse ? { fontWeight: 600, color: $theme.colors.textPulseStrong } : { fontWeight: 700 },
)

export const StyledSentDetails = styled<'div', PulseProp>('div', ({ $theme, $pulse }) => ({
  color: $pulse ? $theme.colors.textPulseLabel : $theme.colors.textSecondaryAlt,
}))
